import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import IsbnScanner from "../components/barcodescanner";
import { useSelector, useDispatch } from 'react-redux';
import { addBook, updateBook, deleteBook, updateBookCover, loanBook, returnBook, reserveBook, unreserveBook, setScanModalOpen, setCurrentBook, setBooks, fetchCategories, addCategoryThunk, updateCategoryThunk, deleteCategoryThunk, setSearchQuery } from '../store/slices/booksSlice';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Filter, Heart, MoreVertical, Newspaper, Plus, ScanLine, Camera, Printer, X, Check, BookOpen, Upload, Calendar, Building, User, CheckCircle, HelpCircle, FileText, Box, Pencil, Trash, Save, MapPin, Activity, Copy, Lock, Unlock, Clock, QrCode, TrendingUp, ArrowUpDown } from 'lucide-react';
import QRCode from 'react-qr-code';
import MemberDetailsModal from '../components/MemberDetailsModal';
import createBook from '../api/CreateBook.js';
import createBookReview from '../api/CreateBookReview.js';
import createOther from '../api/CreateOther.js';
import getBooks from '../api/GetBooks.js';
import uploadImage from '../api/UploadImage.js';
import duplicateItem from '../api/DuplicateItem.js';
import deleteItem from '../api/DeleteItem.js';
import updateWork from '../api/UpdateWork.js';
import returnLoan from '../api/ReturnLoan.js';
import PrintQRModal from '../components/PrintQRModal';
import ConfirmationModal from '../components/ConfirmationModal';
import createLoan from '../api/CreateLoan';
import getLastLoanBook from '../api/GetLastLoanBook';
import getIsbnInfo from '../api/GetISBNInfo.js';
import getLoansList from '../api/GetLoansList.js';
import uploadImageFromUrl from '../api/UploadImageFromUrl.js';
import createReservation from '../api/CreateReservation';
import deleteReservation from '../api/DeleteReservation';
import getReservations from '../api/GetReservations';


const DEFAULT_COVER = '/images/default_cover_w299.jpg';


const BookCard = ({ ID, type, tag, item_id, qrcode_data, title, author, cover, category, rating, status, publisher, year, summary, borrower, onPrintQR, onClick, delay, currentUserRole, copiesCount }) => (

    <motion.div
        layout
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay, duration: 0.3 }}
        whileHover={{ y: -8 }}
        onClick={() => onClick({ ID, item_id, title, author, cover, category, rating, status, publisher, year, summary, borrower })}
        className="bg-card backdrop-blur-xl border border-border rounded-2xl overflow-hidden group relative cursor-pointer"
    >
        <div className="aspect-[2/3] relative overflow-hidden">
            <img onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = DEFAULT_COVER;
            }} src={cover || DEFAULT_COVER} alt={title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent opacity-60" />
            <div className="absolute top-3 right-3 flex gap-2">
                {currentUserRole !== 'invité' && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onPrintQR({ title, ID, qrcode_data, item_id }); }}
                        className="p-2 bg-black/40 backdrop-blur-md rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-primary hover:text-white"
                        title="Imprimer QR Code"
                    >
                        <Printer size={16} />
                    </button>
                )}
            </div>
            <div className="absolute bottom-3 left-3">
                <span className={`px-2 py-1 rounded-md text-xs font-medium ${status === 'Disponible' ? 'bg-emerald-500/40 text-emerald-400 border border-emerald-500/40' :
                    status === 'Réservé' ? 'bg-amber-500/40 text-amber-400 border border-amber-500/40' :
                        'bg-red-500/40 text-red-400 border border-red-500/40'
                    }`}>
                    {status}
                </span>
            </div>
            {copiesCount > 1 && (
                <div className="absolute top-3 left-3 bg-primary text-primary-foreground px-2 py-0.5 rounded-full text-[10px] font-bold shadow-lg border border-primary-foreground/20 flex items-center gap-1">
                    <Copy size={10} />
                    {copiesCount} exemplaires
                </div>
            )}
        </div>

        <div className="p-4">
            <div className="flex items-start justify-between mb-2">
                <div>
                    {type === 'book' ? <h3 className="text-foreground font-bold truncate pr-2">{title}</h3>
                        : type === 'review' ? <h3 className="text-foreground font-bold truncate pr-2">{title + " - " + tag}</h3>
                            : <h3 className="text-foreground font-bold truncate pr-2">{title}</h3>
                    }
                    <p className="text-muted-foreground text-sm">{author}</p>
                </div>
            </div>

            <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
                <span className="px-2 py-1 rounded-lg bg-muted/50 border border-border">{category}</span>
                <div style={{ visibility: rating == "true" ? 'visible' : 'hidden' }} className="flex items-center gap-1 text-yellow-400">
                    <Heart size={12} fill="currentColor" />
                    {/*<span>{rating}</span>*/}
                </div>
            </div>
        </div>
    </motion.div>
);

const BookDetailsModal = ({ isOpen, onClose, book, currentUser, onUpdateCover, onMemberClick, onLoanClick, onReserveForMemberClick, onPrintQR, onManageCategories }) => {
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);
    const API_BASE = useSelector((state) => state.user.apiBase);
    const code_etablissement = useSelector((state) => state.user.currentUser.code_etablissement);
    const enableReservation = useSelector(state => state.user.enableReservation);

    const token = useSelector((state) => state.user.currentUser.token);
    const { categoryBooks, items: books } = useSelector((state) => state.books);

    const uniqueLocations = useMemo(() => {
        if (!books) return [];
        const locations = books.map(b => b.emplacement).filter(Boolean);
        return [...new Set(locations)].sort((a, b) => a.localeCompare(b));
    }, [books]);

    // Edit Mode State
    const [isEditing, setIsEditing] = useState(false);
    const [editFormData, setEditFormData] = useState(null);
    const [lastLoan, setLastLoan] = useState(null);
    const [reservations, setReservations] = useState([]);
    const [isReservedByMe, setIsReservedByMe] = useState(false);


    useEffect(() => {
        if (book && isOpen) {
            const fetchLastLoan = async () => {
                try {
                    const id = book.item_id || book.id;
                    const data = await getLastLoanBook(API_BASE, token, id);
                    // API returns an array of loans, take the first one
                    setLastLoan(Array.isArray(data) ? data[0] : data);
                } catch (error) {
                    console.error("Failed to fetch last loan:", error);
                    setLastLoan(null);
                }
            };
            fetchLastLoan();
        }
    }, [book, isOpen, API_BASE, token]);

    // Confirmation Modal State
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: null,
        isDanger: false,
        confirmText: 'Confirmer'
    });


    const [borrower, setBorrower] = useState(null);
    const [loanCount, setLoanCount] = useState(0);
    const { items: members } = useSelector((state) => state.members);




    useEffect(() => {
        if (book) {
            setEditFormData({
                title: book.title,
                type: book.type,
                tag: book.tag,
                author: book.author,
                category: book.category,
                publisher: book.publisher,
                year: book.year,
                summary: book.description,
                status: book.status,
                emplacement: book.emplacement,
                condition: book.condition,
                isbn: book.isbn || (book.expressions?.[0]?.manifestations?.[0]?.isbn || ""),
                rating: book.rating || "false",
            });
            setIsEditing(false);

            // Fetch borrower if book is borrowed
            if (book.status.toLowerCase() === 'emprunté' && (book.item_id || book.id)) {
                const fetchBorrower = async () => {
                    try {
                        const loan = await getLastLoanBook(API_BASE, token, book.item_id || book.id);
                        if (loan && loan[0].user_id) {
                            const member = members.find(m => (m.ID || m.id) == loan[0].user_id);
                            setBorrower(member || null);
                        }
                    } catch (error) {
                        console.error("Failed to fetch borrower/reserver:", error);
                        setBorrower(null);
                    }
                };
                fetchBorrower();
            } else if (book.status === 'Réservé' && (book.item_id || book.id)) {
                const fetchReserver = async () => {
                    try {
                        const loan = await getLastLoanBook(API_BASE, token, book.item_id || book.id);
                        if (loan && loan[0].user_id) {
                            const member = members.find(m => (m.ID || m.id) == loan[0].user_id);
                            setBorrower(member || null);
                        }
                    } catch (error) {
                        console.error("Failed to fetch reserver:", error);
                        setBorrower(null);
                    }
                };
                fetchReserver();
            } else {
                setBorrower(null);
            }


            const fetchLoanHistory = async () => {
                try {
                    const loans = await getLoansList(API_BASE, token, code_etablissement);
                    if (loans) {
                        const bookLoans = loans.filter(loan => loan.item_id === (parseInt(book.item_id)));
                        setLoanCount(bookLoans.length);
                    }
                } catch (error) {
                    console.error("Failed to fetch loan history:", error);
                }
            };
            fetchLoanHistory();
        }
    }, [book, API_BASE, token, members, code_etablissement]);

    useEffect(() => {
        if (book && isOpen) {
            const fetchReservations = async () => {
                try {
                    const data = await getReservations(API_BASE, token, { item_id: book.item_id || book.id });
                    setReservations(data);
                    const myRes = data.find(r => r.user_id == (currentUser.id || currentUser.ID) && r.status === "Pending");
                    setIsReservedByMe(!!myRes);
                } catch (error) {
                    console.error("Failed to fetch reservations:", error);
                }
            };
            fetchReservations();
        }
    }, [book, isOpen, API_BASE, token, currentUser]);

    const lastBorrower = useMemo(() => {
        if (!lastLoan || !lastLoan.user_id) return null;
        return members.find(m => (m.ID || m.id) == lastLoan.user_id);
    }, [lastLoan, members]);

    if (!isOpen || !book) return null;

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            try {
                // Upload to Go server
                //console.log(API_BASE);
                const imageUrl = await uploadImage(API_BASE, file, currentUser.token, book.manifestation_id);
                // Update with returned URL
                onUpdateCover(book.ID || book.id, imageUrl);
            } catch (error) {
                console.error("Upload failed", error);
                // alert("Erreur lors de l'upload de l'image");
            }
        }
    };

    const handleReturnBook = async () => {
        try {
            try {
                await returnLoan(API_BASE, token, {
                    item_id: parseInt(book.item_id || book.id, 10),
                    code_etablissement: code_etablissement
                });
            } catch (error) {
                console.error("Retour failed:", error);
                alert("Erreur lors du retour du livre: " + error.message);
            }
            dispatch(returnBook({ bookId: book.ID || book.id }));

            // Construct updated book object to update local state immediately
            const updatedBook = { ...book, status: 'Disponible', borrower: null };
            dispatch(setCurrentBook(updatedBook));

        } catch (error) {
            console.error("Retour failed:", error);
            alert("Erreur lors du retour du livre: " + error.message);
        }
    };
    const handleCreateReservation = async () => {
        try {
            await createReservation(API_BASE, token, {
                item_id: parseInt(book.item_id || book.id, 10),
                user_id: parseInt(currentUser.id || currentUser.ID, 10),
                code_etablissement: code_etablissement
            });
            setIsReservedByMe(true);
            alert("Livre réservé ! Vous recevrez un email quand il sera de retour.");
            // Refresh reservations
            const data = await getReservations(API_BASE, token, { item_id: book.item_id || book.id });
            setReservations(data);
        } catch (error) {
            console.error("Reservation failed:", error);
            alert("Erreur lors de la réservation : " + error.message);
        }
    };

    const handleCancelReservation = async () => {
        const myRes = reservations.find(r => r.user_id == (currentUser.id || currentUser.ID) && r.status === "Pending");
        if (!myRes) return;
        try {
            await deleteReservation(API_BASE, token, myRes.ID || myRes.id);
            setIsReservedByMe(false);
            // Refresh reservations
            const data = await getReservations(API_BASE, token, { item_id: book.item_id || book.id });
            setReservations(data);
        } catch (error) {
            console.error("Cancel reservation failed:", error);
        }
    };

    const handleDeleteReservation = async (id) => {
        if (!window.confirm("Supprimer cette réservation ?")) return;
        try {
            await deleteReservation(API_BASE, token, id);
            // Refresh reservations
            const data = await getReservations(API_BASE, token, { item_id: book.item_id || book.id });
            setReservations(data);
        } catch (error) {
            console.error("Delete reservation failed:", error);
        }
    };


    const handleSaveReservation = async (newStatus) => {
        try {
            const payload = {
                ID: parseInt(book.work_id, 10),
                title: book.title,
                original_language: book.original_language || "fr",
                genre: book.category,
                code_etablissement: code_etablissement,
                expressions: [
                    {
                        ID: parseInt(book.expressions[0].ID, 10),
                        title: book.title,
                        language: "fr",
                        manifestations: [
                            {
                                ID: parseInt(book.manifestation_id || 0, 10),
                                title: book.title,
                                publisher: book.publisher,
                                pub_date: book.year,
                                isbn: book.isbn || (book.expressions?.[0]?.manifestations?.[0]?.isbn || ""),
                                preview_link: book.rating || "false",
                                description: book.description,
                                items: [
                                    {
                                        ID: parseInt(book.item_id || book.id, 10),
                                        location: book.emplacement,
                                        condition: book.condition,
                                        code_etablissement: code_etablissement,
                                        status: newStatus
                                    }
                                ]
                            }
                        ]
                    }
                ]
            };

            if (newStatus === 'Réservé') {
                // Also create a loan record for the reservation to track the student
                try {
                    await createLoan(API_BASE, token, {
                        item_id: parseInt(book.item_id || book.id, 10),
                        user_id: parseInt(currentUser.id || currentUser.ID, 10),
                        start_date: new Date().toISOString(),
                        end_date: null,
                        code_etablissement: code_etablissement
                    });
                } catch (loanError) {
                    console.error("Failed to create loan record for reservation:", loanError);
                }
            }

            // Call updateWork AFTER createLoan to ensure the status 'Réservé' isn't overwritten to 'emprunté' by the backend
            await updateWork(API_BASE, token, payload);

            if (newStatus === 'Réservé') {
                dispatch(reserveBook({ bookId: book.ID || book.id }));
            } else {
                dispatch(unreserveBook({ bookId: book.ID || book.id }));
            }

            // Refresh books list to be sure
            const data = await getBooks(API_BASE, token, code_etablissement);
            if (data && data.work) {
                dispatch(setBooks(data.work));
            }
        } catch (error) {
            console.error("Reservation failed:", error);
            alert("Erreur lors de la réservation du livre.");
        }
    };

    const handleSave = async () => {
        try {
            //console.log("Updating book:", book);
            // Construct payload matching the Go struct
            const payload = {
                ID: parseInt(book.work_id, 10),
                title: editFormData.title,
                original_language: book.original_language || "fr", // Preserve or default
                genre: editFormData.category,
                code_etablissement: code_etablissement,
                contributions: book.contributions?.map(c => {
                    if (c.role === "Auteur") {
                        return {
                            ...c,
                            agent: {
                                ...c.agent,
                                name: editFormData.author
                            }
                        };
                    }
                    return c;
                }) || [
                        {
                            role: "Auteur",
                            agent: { name: editFormData.author, type: "Personne" }
                        }
                    ],
                expressions: [
                    {
                        ID: parseInt(book.expressions[0].ID, 10), // Need to ensure we have these IDs from getBooks
                        title: editFormData.title,
                        language: "fr",
                        manifestations: [
                            {
                                ID: parseInt(book.manifestation_id || 0, 10),
                                title: editFormData.title, // Often same as work title
                                publisher: editFormData.publisher,
                                pub_date: editFormData.year,
                                isbn: editFormData.isbn,
                                preview_link: editFormData.rating,
                                description: editFormData.summary,
                                items: [
                                    {
                                        ID: parseInt(book.item_id || book.id, 10),
                                        location: editFormData.emplacement,
                                        condition: editFormData.condition,
                                        code_etablissement: code_etablissement,
                                        status: editFormData.status
                                    }
                                ],
                                type: editFormData.type,
                                tag: editFormData.tag,
                            }
                        ]
                    }
                ]
            };

            await updateWork(API_BASE, token, payload);

            // Refresh books list
            const data = await getBooks(API_BASE, token, code_etablissement);
            if (data && data.work) {
                dispatch(setBooks(data.work));
            }

            dispatch(updateBook({
                id: book.ID || book.id,
                ...editFormData,
                status: editFormData.status, // Ensure status is correctly passed
                description: editFormData.summary
            }));
            setIsEditing(false);
        } catch (error) {
            console.error("Update failed:", error);
            alert("Erreur lors de la mise à jour du livre.");
        }
    };

    const performDelete = async () => {
        try {
            // Use item_id (which is real DB ID) if available, fallback to id
            const itemId = book.item_id || book.id;
            await deleteItem(itemId);

            // Refresh books list
            const data = await getBooks(API_BASE, token, code_etablissement);
            if (data && data.work) {
                dispatch(setBooks(data.work));
            }

            onClose();
        } catch (error) {
            console.error("Suppression failed:", error);
            alert("Erreur lors de la suppression du livre.");
        }
        dispatch(deleteBook(book.ID || book.id));
        onClose();
    };

    const handleDelete = () => {
        setConfirmModal({
            isOpen: true,
            title: "Supprimer le livre",
            message: "Êtes-vous sûr de vouloir supprimer ce livre ? Cette action est irréversible.",
            onConfirm: performDelete,
            isDanger: true,
            confirmText: 'Supprimer'
        });
    };

    const performDuplicate = async () => {
        try {
            // Use item_id (which is real DB ID) if available, fallback to id
            const itemId = book.item_id || book.id;
            await duplicateItem(itemId);

            // Refresh books list
            const data = await getBooks(API_BASE, token, code_etablissement);
            if (data && data.work) {
                dispatch(setBooks(data.work));
            }

            onClose();
            //alert("Livre dupliqué avec succès !");
        } catch (error) {
            console.error("Duplication failed:", error);
            alert("Erreur lors de la duplication du livre.");
        }
    };

    const handleDuplicate = () => {
        setConfirmModal({
            isOpen: true,
            title: "Dupliquer le livre",
            message: `Voulez-vous dupliquer le livre "${book.title}" ?`,
            onConfirm: performDuplicate,
            isDanger: false,
            confirmText: 'Dupliquer'
        });
    };

    return (
        <div className="fixed inset-0 z-50 flex justify-end pointer-events-none">
            <motion.div
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                className="w-full max-w-2xl bg-background border-l border-border h-full shadow-2xl overflow-hidden flex flex-col relative pointer-events-auto"
            >
                {/* Cover Image Header */}
                <div className="h-64 relative shrink-0 group">
                    <img
                        src={book.cover || DEFAULT_COVER}
                        alt={book.title}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />

                    {/* Cover Upload Button */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40">
                        {isEditing ? <div className="text-center">
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="bg-white/10 hover:bg-white/20 backdrop-blur-md text-white p-3 rounded-full border border-white/20 transition-all transform hover:scale-110 mb-2"
                            >
                                <Upload size={24} />
                            </button>
                            <p className="text-white text-sm font-medium drop-shadow-md">Changer la couverture</p>
                            <input
                                type="file"
                                ref={fileInputRef}
                                className="hidden"
                                accept="image/*"
                                onChange={handleFileChange}
                            />
                        </div> : null}
                    </div>

                    {/* Close Button */}
                    <button onClick={onClose} className="absolute top-4 right-4 p-2 bg-black/40 backdrop-blur-md rounded-full text-white hover:bg-black/60 transition-colors z-10">
                        <X size={24} />
                    </button>
                </div>

                {/* Details Section */}
                <div className="flex-1 p-8 relative flex flex-col overflow-y-auto">
                    <div className="flex justify-end gap-2 mb-4 absolute top-6 right-6 z-10">
                        {currentUser?.role === 'Bibliothécaire' && (
                            <>
                                {isEditing ? (
                                    <div style={{ marginBottom: "5px" }}>
                                        <button
                                            style={{ marginRight: "5px" }}
                                            onClick={handleSave}
                                            className="p-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors"
                                            title="Enregistrer"
                                        >
                                            <Save size={20} />
                                        </button>
                                        <button
                                            onClick={() => setIsEditing(false)}
                                            className="p-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
                                            title="Annuler"
                                        >
                                            <X size={20} />
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <button
                                            onClick={handleDuplicate}
                                            className="p-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors"
                                            title="Dupliquer"
                                        >
                                            <Copy size={20} />
                                        </button>
                                        <button
                                            onClick={() => onPrintQR({ ...book, item_id: book.item_id || book.id })}
                                            className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                                            title="Imprimer QR Code"
                                        >
                                            <Printer size={20} />
                                        </button>
                                        <button
                                            onClick={() => setIsEditing(true)}
                                            className="p-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30 transition-colors"
                                            title="Modifier"
                                        >
                                            <Pencil size={20} />
                                        </button>
                                        <button
                                            onClick={handleDelete}
                                            disabled={book.status.toLowerCase() === 'emprunté'}
                                            style={{ opacity: book.status.toLowerCase() === 'emprunté' ? 0.3 : 1, cursor: book.status.toLowerCase() === 'emprunté' ? 'not-allowed' : 'pointer' }}
                                            className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                                            title="Supprimer"
                                        >
                                            <Trash size={20} />
                                        </button>
                                    </>
                                )}
                            </>
                        )}
                        <button onClick={onClose} className="p-2 text-muted-foreground hover:text-foreground transition-colors">
                            <X size={24} />
                        </button>
                    </div>

                    <div className="flex items-center gap-3 mb-4">
                        <span className="px-3 py-1 rounded-lg bg-primary/10 text-primary text-xs font-medium border border-primary/20">
                            {book.category}
                        </span>
                        <div style={{ visibility: book.rating == "true" ? 'visible' : 'hidden' }} className="flex items-center gap-1 text-yellow-400 text-sm">
                            <Heart size={14} fill="currentColor" />
                            {/*<span>{book.preview_link}</span>*/}
                        </div>
                    </div>

                    {!isEditing && currentUser?.role !== 'invité' && (
                        <div className="mb-6">
                            {book.status.toLowerCase() === 'emprunté' && currentUser?.role === 'Bibliothécaire' ? (
                                <div className="flex gap-3">
                                    <button
                                        onClick={handleReturnBook}
                                        className="px-6 py-2 rounded-xl font-bold transition-all bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 border border-orange-500/20 text-sm shadow-lg shadow-orange-500/10"
                                    >
                                        Rendre ce livre
                                    </button>
                                    <button
                                        onClick={() => onReserveForMemberClick(book)}
                                        className="px-6 py-2 rounded-xl font-bold transition-all bg-primary/20 text-primary hover:bg-primary/30 border border-primary/20 text-sm shadow-lg shadow-primary/10"
                                    >
                                        Réserver pour un élève
                                    </button>
                                </div>
                            ) : book.status.toLowerCase() === 'emprunté' && currentUser?.role === 'Étudiant' ? (
                                <button
                                    onClick={isReservedByMe ? handleCancelReservation : handleCreateReservation}
                                    className={`px-6 py-2 rounded-xl font-bold transition-all text-sm shadow-lg ${isReservedByMe
                                        ? 'bg-amber-500/20 text-amber-400 border border-amber-500/20 shadow-amber-500/10'
                                        : 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-primary/20'
                                        }`}
                                >
                                    {isReservedByMe ? 'Annuler ma réservation' : 'Réserver ce livre'}
                                </button>
                            ) : book.status === 'Réservé' ? (
                                <button
                                    onClick={() => {
                                        if (currentUser?.role === 'Bibliothécaire' || currentUser?.role === 'Étudiant') {
                                            dispatch(reserveBook({ bookId: book.ID || book.id, status: 'Disponible' }));
                                            handleSaveReservation('Disponible');
                                        }
                                    }}
                                    className="px-6 py-2 rounded-xl font-bold transition-all bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border border-amber-500/20 text-sm shadow-lg shadow-amber-500/10"
                                >
                                    {currentUser?.role === 'Étudiant' ? 'Annuler ma réservation' : 'Annuler la réservation'}
                                </button>
                            ) : (
                                !enableReservation && currentUser?.role === 'Étudiant' ? null : <button
                                    onClick={() => {
                                        if (book.status === 'Disponible') {
                                            if (currentUser?.role === 'Étudiant') {
                                                handleSaveReservation('Réservé');
                                            } else {
                                                onLoanClick(book);
                                            }
                                        }
                                    }}
                                    className={`px-6 py-2 rounded-xl font-bold transition-all text-sm shadow-lg ${book.status === 'Disponible'
                                        ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-primary/20'
                                        : 'bg-muted text-muted-foreground cursor-not-allowed border border-border shadow-none'
                                        }`}>
                                    {book.status === 'Disponible'
                                        ? (currentUser?.role === 'Étudiant' ? 'Réserver ce livre' : 'Emprunter ce livre')
                                        : book.status}
                                </button>
                            )}
                        </div>
                    )}

                    {isEditing ? (
                        <input
                            type="text"
                            value={editFormData?.title || ''}
                            onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                            className="w-full bg-muted border border-border rounded-lg px-4 py-2 text-foreground font-bold text-2xl mb-2"
                            placeholder="Titre du livre"
                        />
                    ) : (
                        <h2 className="text-3xl font-bold text-foreground mb-2">{book.title}</h2>
                    )}
                    {isEditing ? (
                        <>
                            <input
                                type="text"
                                value={editFormData?.author || ''}
                                onChange={(e) => setEditFormData({ ...editFormData, author: e.target.value })}
                                className="w-full bg-muted border border-border rounded-lg px-4 py-2 text-xl text-foreground mb-4"
                                placeholder="Auteur"
                            />
                            <div className="grid grid-cols-2 gap-4 mt-4">
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <label className="block text-muted-foreground text-sm">Catégorie</label>
                                        <button
                                            type="button"
                                            onClick={onManageCategories}
                                            className="text-[10px] text-primary hover:underline flex items-center gap-1"
                                        >
                                            <Plus size={10} /> Gérer
                                        </button>
                                    </div>
                                    <select
                                        value={editFormData?.category || ''}
                                        onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })}
                                        className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground"
                                    >
                                        <option value="">Sélectionner...</option>
                                        {[...categoryBooks].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map(cat => (
                                            <option key={cat.ID || cat.name} value={cat.name}>{cat.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-muted-foreground text-sm mb-1">Statut</label>
                                    <select
                                        value={editFormData?.status || ''}
                                        onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                                        className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground"
                                    >
                                        <option value="Disponible">Disponible</option>
                                        <option value="emprunté">Emprunté</option>
                                        <option value="Réservé">Réservé</option>
                                        <option value="Perdu">Perdu</option>
                                        <option value="Archivé">Archivé</option>
                                    </select>
                                </div>
                                <div className="col-span-1">
                                    <label className="block text-muted-foreground text-sm mb-1">Coup de coeur</label>
                                    <button
                                        onClick={() => setEditFormData({ ...editFormData, rating: editFormData.rating == "true" ? "false" : "true" })}
                                        className={`w-full py-2 rounded-lg flex items-center justify-center gap-2 border transition-colors ${editFormData?.rating == "true"
                                            ? 'bg-red-500/20 border-red-500/50 text-red-500'
                                            : 'bg-muted border-border text-muted-foreground hover:bg-muted/80'
                                            }`}
                                    >
                                        <Heart size={18} fill={editFormData?.rating == "true" ? "currentColor" : "none"} />
                                        {editFormData?.rating == "true" ? 'Oui' : 'Non'}
                                    </button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <p
                            className="text-xl text-muted-foreground hover:text-primary cursor-pointer hover:underline transition-colors block w-fit"
                            onClick={() => {
                                dispatch(setSearchQuery(book.author));
                                onClose();
                            }}
                            title="Voir tous les livres de cet auteur"
                        >
                            {book.author}
                        </p>
                    )}
                    {!isEditing && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-2 bg-muted/30 w-fit px-2 py-1 rounded-md border border-border/50">
                            <QrCode size={14} />
                            <span className="font-mono">ID: {book.item_id || book.id || book.ID}</span>
                        </div>
                    )}


                    <div className="grid grid-cols-2 gap-4 mb-8">
                        <div className="bg-muted/50 p-4 rounded-xl border border-border">
                            <div className="flex items-center gap-2 text-muted-foreground mb-1 text-sm">
                                <Building size={16} />
                                <span>Éditeur</span>
                            </div>
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={editFormData?.publisher || ''}
                                    onChange={(e) => setEditFormData({ ...editFormData, publisher: e.target.value })}
                                    className="w-full bg-muted border border-border rounded px-2 py-1 text-foreground font-medium"
                                />
                            ) : (
                                <p className="text-foreground font-medium">{book.publisher || "Éditions Gallimard"}</p>
                            )}
                        </div>
                        <div className="bg-muted/50 p-4 rounded-xl border border-border">
                            <div className="flex items-center gap-2 text-muted-foreground mb-1 text-sm">
                                <Calendar size={16} />
                                <span>Date de publication</span>
                            </div>
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={editFormData?.year || ''}
                                    onChange={(e) => setEditFormData({ ...editFormData, year: e.target.value })}
                                    className="w-full bg-muted border border-border rounded px-2 py-1 text-foreground font-medium"
                                />
                            ) : (
                                <p className="text-foreground font-medium">{book.year || "2023"}</p>
                            )}
                        </div>
                        {book.type == "books" && <div className="bg-muted/50 p-4 rounded-xl border border-border">
                            <div className="flex items-center gap-2 text-muted-foreground mb-1 text-sm">
                                <ScanLine size={16} />
                                <span>ISBN</span>
                            </div>
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={editFormData?.isbn || ''}
                                    onChange={(e) => setEditFormData({ ...editFormData, isbn: e.target.value })}
                                    className="w-full bg-muted border border-border rounded px-2 py-1 text-foreground font-medium"
                                />
                            ) : (
                                <p className="text-foreground font-medium">{editFormData?.isbn || book.isbn || (book.expressions?.[0]?.manifestations?.[0]?.isbn || "Non renseigné")}</p>
                            )}
                        </div>}
                        {book.type == "review" && <div className="bg-muted/50 p-4 rounded-xl border border-border">
                            <div className="flex items-center gap-2 text-muted-foreground mb-1 text-sm">
                                <Newspaper size={16} />
                                <span>Numéro</span>
                            </div>
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={editFormData?.tag || ''}
                                    onChange={(e) => setEditFormData({ ...editFormData, tag: e.target.value })}
                                    className="w-full bg-muted border border-border rounded px-2 py-1 text-foreground font-medium"
                                />
                            ) : (
                                <p className="text-foreground font-medium">{editFormData?.tag || book.tag || (book.expressions?.[0]?.manifestations?.[0]?.tag || "Non renseigné")}</p>
                            )}
                        </div>}
                        <div className="bg-muted/50 p-4 rounded-xl border border-border">
                            <div className="flex items-center gap-2 text-muted-foreground mb-1 text-sm">
                                <MapPin size={16} />
                                <span>Emplacement</span>
                            </div>
                            {isEditing ? (
                                <>
                                    <input
                                        type="text"
                                        list="locations-list"
                                        value={editFormData?.emplacement || ''}
                                        onChange={(e) => setEditFormData({ ...editFormData, emplacement: e.target.value })}
                                        className="w-full bg-muted border border-border rounded px-2 py-1 text-foreground font-medium"
                                        placeholder="Sélectionner ou saisir..."
                                    />
                                    <datalist id="locations-list">
                                        {uniqueLocations.map(loc => (
                                            <option key={loc} value={loc} />
                                        ))}
                                    </datalist>
                                </>
                            ) : (
                                <p className="text-foreground font-medium">{book.emplacement || "Non renseigné"}</p>
                            )}
                        </div>
                        <div className="bg-muted/50 p-4 rounded-xl border border-border">
                            <div className="flex items-center gap-2 text-muted-foreground mb-1 text-sm">
                                <Activity size={16} />
                                <span>État</span>
                            </div>
                            {isEditing ? (
                                <select
                                    value={editFormData?.condition || ''}
                                    onChange={(e) => setEditFormData({ ...editFormData, condition: e.target.value })}
                                    className="w-full bg-muted border border-border rounded px-2 py-1 text-foreground font-medium"
                                >
                                    <option value="Neuf">Neuf</option>
                                    <option value="Très bon">Très bon</option>
                                    <option value="Bon">Bon</option>
                                    <option value="Moyen">Moyen</option>
                                    <option value="Mauvais">Mauvais</option>
                                </select>
                            ) : (
                                <p className="text-foreground font-medium">{book.condition || "Bon"}</p>
                            )}
                        </div>
                        <div className="bg-muted/50 p-4 rounded-xl border border-border flex flex-col justify-between">
                            <div className="flex items-center gap-2 text-muted-foreground mb-1 text-sm">
                                <CheckCircle size={16} />
                                <span>Total des emprunts</span>
                            </div>
                            <div>
                                <p className="text-foreground font-medium">{loanCount} fois</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {lastLoan && lastLoan.start_date
                                        ? `Dernier emprunt le ${new Date(lastLoan.start_date).toLocaleDateString('fr-FR')}`
                                        : "Aucun emprunt enregistré"}
                                </p>
                            </div>
                        </div>
                    </div>

                    {reservations.length > 0 && currentUser?.role !== 'Étudiant' && currentUser?.role !== 'invité' && (
                        <div className="mb-8 bg-amber-500/10 border border-amber-500/20 p-4 rounded-xl">
                            <h4 className="text-amber-500 font-bold flex items-center gap-2 mb-3">
                                <Clock size={18} />
                                Réservations en attente ({reservations.length})
                            </h4>
                            <div className="space-y-3">
                                {reservations.map((res, idx) => (
                                    <div key={res.ID || res.id} className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <img
                                                src={res.user?.avatar || (res.user?.role === 'Étudiant' ? '/avatars/student.png' : '/avatars/librarian.png')}
                                                alt=""
                                                className="w-8 h-8 rounded-full object-cover border border-amber-500/20"
                                                onError={(e) => { e.target.src = '/avatars/student.png'; }}
                                            />
                                            <div>
                                                <p className="text-foreground text-sm font-medium">{res.user?.name}</p>
                                                <p className="text-muted-foreground text-[10px]">{res.user?.role} • {new Date(res.CreatedAt).toLocaleDateString()}</p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleDeleteReservation(res.ID || res.id)}
                                            className="text-muted-foreground hover:text-red-500 transition-colors"
                                        >
                                            <Trash size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}


                    {(book.status.toLowerCase() === 'emprunté' || book.status === 'Réservé') && borrower && currentUser?.role !== 'Étudiant' && currentUser?.role !== 'invité' && (
                        <div
                            onClick={() => onMemberClick(borrower)}
                            className="mb-8 bg-primary/10 border border-primary/20 p-4 rounded-xl cursor-pointer hover:bg-primary/20 transition-colors group"
                        >
                            <div className="flex items-center justify-between mb-2">
                                <h4 className="text-primary font-bold flex items-center gap-2">
                                    <User size={18} />
                                    {book.status === 'Réservé' ? 'Réservé par' : 'Emprunté par'}
                                </h4>
                                <span className="text-xs text-primary/70 group-hover:text-primary transition-colors">Voir la fiche &rarr;</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <img
                                    src={borrower.avatar || (borrower.role === 'Étudiant' ? '/avatars/student.png' : '/avatars/librarian.png')}
                                    alt={borrower.name}
                                    className="w-10 h-10 rounded-full border border-primary/30 object-cover"
                                    onError={(e) => { e.target.src = '/avatars/student.png'; }}
                                />
                                <div>
                                    <p className="text-foreground font-medium">{borrower.name}</p>
                                    <p className="text-muted-foreground text-xs">{borrower.role}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {currentUser?.role === 'Bibliothécaire' && book.status.toLowerCase() !== 'emprunté' && book.status !== 'Réservé' && lastBorrower && (
                        <div
                            onClick={() => onMemberClick(lastBorrower)}
                            className="mb-8 bg-muted/40 border border-border p-4 rounded-xl cursor-pointer hover:bg-muted/60 transition-colors group"
                        >
                            <div className="flex items-center justify-between mb-2">
                                <h4 className="text-muted-foreground font-bold flex items-center gap-2 text-sm">
                                    <User size={18} className="text-primary" />
                                    Dernier emprunteur
                                </h4>
                                <span className="text-xs text-primary/70 group-hover:text-primary transition-colors">Voir la fiche &rarr;</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <img
                                    src={lastBorrower.avatar || (lastBorrower.role === 'Étudiant' ? '/avatars/student.png' : '/avatars/librarian.png')}
                                    alt={lastBorrower.name}
                                    className="w-10 h-10 rounded-full border border-border object-cover"
                                    onError={(e) => { e.target.src = '/avatars/student.png'; }}
                                />
                                <div>
                                    <p className="text-foreground font-medium">{lastBorrower.name}</p>
                                    <p className="text-muted-foreground text-xs">
                                        {lastBorrower.role}
                                        {lastLoan && lastLoan.start_date && (
                                            <> • Emprunté le {new Date(lastLoan.start_date).toLocaleDateString('fr-FR')}</>
                                        )}
                                        {lastLoan && lastLoan.end_date && !lastLoan.end_date.startsWith("0001") && (
                                            <> au {new Date(lastLoan.end_date).toLocaleDateString('fr-FR')}</>
                                        )}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="mb-8">
                        <h3 className="text-lg font-bold text-foreground mb-3">Résumé</h3>
                        {isEditing ? (
                            <textarea
                                value={editFormData?.summary || ''}
                                onChange={(e) => setEditFormData({ ...editFormData, summary: e.target.value })}
                                className="w-full h-32 bg-muted border border-border rounded-lg px-4 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                            />
                        ) : (
                            <p className="text-muted-foreground leading-relaxed">
                                {book.description}
                            </p>
                        )}
                    </div>


                </div>
            </motion.div>
            <ConfirmationModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
                onConfirm={confirmModal.onConfirm}
                title={confirmModal.title}
                message={confirmModal.message}
                isDanger={confirmModal.isDanger}
                confirmText={confirmModal.confirmText}
            />
        </div>
    );
};
const MemberSelectionModal = ({ isOpen, onClose, onSelect }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [scanQuery, setScanQuery] = useState('');
    const scanInputRef = useRef(null);

    const { items: members } = useSelector((state) => state.members);

    useEffect(() => {
        if (isOpen) {
            setSearchTerm('');
            setScanQuery('');
            // Optional: convert to scan mode auto-focus if desired, but user asked for fields.
            // scanInputRef.current?.focus(); 
        }
    }, [isOpen]);

    const handleScan = (e) => {
        const value = e.target.value;
        setScanQuery(value);

        // Check for direct ID or USER: prefix
        let memberId = value;
        if (value.startsWith('USER:')) {
            memberId = value.split(':')[1];
        }

        // Try to find exact match
        if (memberId) {
            const member = members.find(m => (m.ID || m.id) == memberId);
            if (member) {
                onSelect(member);
            }
        }
    };

    const filteredMembers = members.filter(m =>
        (m.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (m.email || "").toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-background border border-border rounded-2xl p-6 w-full max-w-md shadow-2xl"
            >
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-foreground">Sélectionner un membre</h3>
                    <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
                        <X size={20} />
                    </button>
                </div>

                <div className="space-y-4 mb-4">
                    {/* Scan Input */}
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <ScanLine className="text-primary animate-pulse" size={18} />
                        </div>
                        <input
                            ref={scanInputRef}
                            type="text"
                            value={scanQuery}
                            onChange={handleScan}
                            placeholder="Scanner un QR Code membre..."
                            className="w-full bg-muted border-2 border-primary/30 rounded-xl py-3 pl-10 pr-4 text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary transition-colors"
                            autoFocus
                        />
                    </div>

                    {/* Search Input */}
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="text-muted-foreground" size={18} />
                        </div>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Rechercher par nom ou email..."
                            className="w-full bg-muted border border-border rounded-xl py-2 pl-10 pr-4 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                        />
                    </div>
                </div>


                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {filteredMembers.sort((a, b) => a.name.localeCompare(b.name)).map(member => (
                        <button
                            key={member.ID || member.id}
                            onClick={() => onSelect(member)}
                            className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-muted/50 transition-colors text-left group border border-transparent hover:border-border"
                        >
                            <img src={member.avatar} alt={member.name} className="w-10 h-10 rounded-full border border-border" />
                            <div>
                                <p className="text-foreground font-medium group-hover:text-primary transition-colors">{member.name}</p>
                                <p className="text-muted-foreground text-xs">{member.role}</p>
                            </div>
                            <div className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-primary">
                                <CheckCircle size={18} />
                            </div>
                        </button>
                    ))}
                </div>
            </motion.div>
        </div>
    );
};

const AddBookModal = ({ isOpen, onClose, onAdd, onDuplicateFound, currentUser, onManageCategories, code_etablissement }) => {
    const dispatch = useDispatch();
    const { items: books, categoryBooks } = useSelector((state) => state.books);
    const API_BASE = useSelector((state) => state.user.apiBase);
    const token = useSelector((state) => state.user.currentUser.token);
    const [resourceType, setResourceType] = useState('book'); // 'book', 'review', 'other'
    const [formData, setFormData] = useState({
        title: '',
        author: '',
        category: '',
        cover: DEFAULT_COVER,
        // New fields for Revue
        tag: '',
        publisher: '',
        pubDate: '',
        issn: '',
        location: '',
        condition: '',
        isbn: '',
        language: 'fr',
        // New fields for Other
        description: '',
        code_etablissement: code_etablissement,
        suggestedCategory: '',
    });

    const existingReviewTitles = useMemo(() => {
        if (!books) return [];
        const titles = books
            .filter(b => b.type === 'review')
            .map(b => b.title)
            .filter(Boolean);
        return [...new Set(titles)].sort();
    }, [books]);

    const uniqueLocations = useMemo(() => {
        if (!books) return [];
        const locations = books.map(b => b.emplacement).filter(Boolean);
        return [...new Set(locations)].sort((a, b) => a.localeCompare(b));
    }, [books]);

    const [isCategoryLocked, setIsCategoryLocked] = useState(false);
    const [isLocationLocked, setIsLocationLocked] = useState(false);

    const [isbnScan, setIsbnScan] = useState('');
    const [databarcode, setDatabarcode] = useState('');
    const [isScannerVisible, setIsScannerVisible] = useState(false);
    const [isFetchingIsbn, setIsFetchingIsbn] = useState(false);
    const scanTimeoutRef = useRef(null);
    const isbnInputRef = useRef(null);
    const ISBNDB_API_KEY = useSelector((state) => state.user.isbndbApiKey);

    const handleIsbnScan = async (isbn) => {
        if (!isbn || isbn.length < 10) return;

        setIsFetchingIsbn(true);
        try {
            const bookInfo = await getIsbnInfo(API_BASE, isbn);
            //console.log(bookInfo);
            if (bookInfo) {
                setFormData(prev => {
                    let pubDate = prev.pubDate;
                    if (bookInfo.publish_date) {
                        pubDate = bookInfo.publish_date;
                    }
                    return {
                        ...prev,
                        title: bookInfo.title || prev.title,
                        author: bookInfo.authors ? bookInfo.authors.join(', ') : (bookInfo.author || prev.author),
                        publisher: bookInfo.publisher || prev.publisher,
                        pubDate: bookInfo.date_published || pubDate,
                        language: bookInfo.language || prev.language,
                        isbn: bookInfo.isbn13 || bookInfo.isbn || isbn,
                        cover: bookInfo.image || prev.cover,
                        description: bookInfo.synopsis || prev.description,
                        suggestedCategory: bookInfo.subjects ? bookInfo.subjects[0] : '',
                        code_etablissement: prev.code_etablissement,
                    };
                });
                setIsbnScan('');
            } else {
                setFormData(prev => {
                    return {
                        ...prev,
                        isbn: isbn,
                        code_etablissement: prev.code_etablissement,
                    };
                });
            }
        } catch (error) {
            console.error(error);
            //alert(error.message);
            setFormData(prev => {
                return {
                    ...prev,
                    isbn: isbn,
                    code_etablissement: prev.code_etablissement,
                };
            });
        } finally {
            setIsFetchingIsbn(false);
            // Refocus input after re-enabling
            setTimeout(() => {
                if (isbnInputRef.current) {
                    isbnInputRef.current.focus();
                }
            }, 300);
        }
    };

    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        //const token = "TON_JWT_ICI"; // récupéré via /auth/login

        if (resourceType === 'book') {
            (async () => {
                try {
                    //avoir la liste avant la création car ensuite le livre existe forcémment
                    const bookData2 = await getBooks(API_BASE, token, code_etablissement);
                    const result = await createBook(API_BASE, token, {
                        "title": formData.title,
                        "genre": formData.category,
                        "original_language": formData.language,
                        "summary": formData.description,
                        "code_etablissement": code_etablissement,
                        "contributions": [
                            {
                                "role": "Auteur",
                                "agent": { "name": formData.author || "Inconnu", "type": "Personne" }
                            }
                        ],
                        "expressions": [
                            {
                                "title": formData.title,
                                "language": formData.language,
                                "manifestations": [
                                    {
                                        "title": "Édition Standard",
                                        "publisher": formData.publisher || "Inconnu",
                                        "pub_date": formData.pubDate || new Date().getFullYear().toString(),
                                        "isbn": formData.isbn || "0000000000000",
                                        "description": formData.description,
                                        "items": [
                                            { "location": formData.location, "condition": formData.condition }
                                        ]
                                    }
                                ]

                            }
                        ]
                    });

                    //console.log("Création Livre OK :", result);

                    let finalCover = formData.cover;

                    // Auto-upload image if it's an external URL
                    if (result && result.work && formData.cover && formData.cover.startsWith('http')) {
                        try {
                            const work = result.work;
                            const manifestationId = work.expressions?.[0]?.manifestations?.[0]?.ID;
                            if (manifestationId) {
                                //console.log("Tentative d'upload de l'image depuis URL...", formData.cover);
                                const newCoverUrl = await uploadImageFromUrl(API_BASE, token, formData.cover, manifestationId);
                                //console.log("Image uploadée avec succès :", newCoverUrl);
                                finalCover = newCoverUrl;
                            }
                        } catch (err) {
                            console.error("Erreur lors de l'upload automatique de l'image:", err);
                        }
                    }



                    const bookData = await getBooks(API_BASE, token, code_etablissement);
                    if (bookData && bookData.work) {
                        dispatch(setBooks(bookData.work));

                        // Test si la manifestation existe déjà parmi la liste des books
                        if (result && result.work) {
                            const newManifestationId = result.work.expressions?.[0]?.manifestations?.[0]?.ID;
                            if (newManifestationId) {
                                const existingBook = bookData2.work.find(b =>
                                    b.expressions?.some(expr =>
                                        expr.manifestations?.some(m => m.ID == newManifestationId)
                                    )
                                );

                                if (existingBook) {
                                    onDuplicateFound(existingBook);
                                    return;
                                }
                            }


                            /*if (result.work.ID) {
                                const existingBookByWork = bookData.work.find(b => b.ID == result.work.ID.toString());
                                if (existingBookByWork) {
                                    onDuplicateFound(existingBookByWork);
                                    return;
                                }
                            }*/
                        }
                    } else {
                        onAdd({
                            ...formData,
                            cover: finalCover,
                            id: result && result.work ? result.work.ID : Math.random().toString(36).substr(2, 9),
                            rating: 0,
                            status: 'Disponible',
                            publisher: 'Nouvel Éditeur',
                            year: new Date().getFullYear().toString(),
                            summary: formData.description || 'Aucun résumé disponible pour le moment.'
                        });
                    }


                } catch (err) {
                    console.error(err);
                }
            })();

        } else if (resourceType === 'review') {
            (async () => {
                try {
                    const result = await createBookReview(API_BASE, token, {
                        "title": formData.title,
                        "genre": formData.category,
                        "original_language": formData.language,
                        "summary": formData.description,
                        "code_etablissement": code_etablissement,
                        "contributions": [
                            {
                                "role": "Auteur",
                                "agent": { "name": formData.author || "Auteurs de revues / Journalistes", "type": "Entreprise" }
                            }
                        ],
                        "expressions": [
                            {
                                "title": formData.title,
                                "language": formData.language,
                                "manifestations": [
                                    {
                                        "title": "Édition Standard",
                                        "publisher": formData.publisher || "Inconnu",
                                        "pub_date": formData.pubDate || new Date().getFullYear().toString(),
                                        "isbn": formData.isbn || "0000000000000",
                                        "description": formData.description,
                                        "items": [
                                            { "location": formData.location, "condition": formData.condition }
                                        ],
                                        "type": "review",
                                        "tag": formData.tag,
                                    }
                                ]

                            }
                        ]
                    });
                    /*const result = await createRevue(API_BASE, token, {
                        title: formData.title,
                        issue: formData.issue,
                        publisher: formData.publisher,
                        pubDate: formData.pubDate,
                        language: "fr",
                        issn: formData.issn,
                        location: formData.location,
                        condition: formData.condition,
                        code_etablissement: code_etablissement
                    });*/
                    //console.log("Création Revue OK :", result);

                    // Reload books
                    const bookData = await getBooks(API_BASE, token, code_etablissement);
                    if (bookData && bookData.work) {
                        dispatch(setBooks(bookData.work));
                    }
                } catch (err) {
                    console.error(err);
                }
            })();
            onAdd({
                ...formData,
                id: Math.random().toString(36).substr(2, 9),
                rating: 0,
                status: 'Disponible',
                category: 'Revue',
                summary: `Numéro: ${formData.issue}`
            });

        } else {
            (async () => {
                try {
                    const result = await createOther(API_BASE, token, {
                        "title": formData.title,
                        "genre": formData.category,
                        "original_language": formData.language,
                        "summary": formData.description,
                        "code_etablissement": code_etablissement,
                        "contributions": [
                            {
                                "role": "Auteur",
                                "agent": { "name": formData.category || "Inconnu", "type": "Personne" }
                            }
                        ],
                        "expressions": [
                            {
                                "title": formData.title,
                                "language": formData.language,
                                "manifestations": [
                                    {
                                        "title": "Édition Standard",
                                        "publisher": formData.publisher || "Inconnu",
                                        "pub_date": formData.pubDate || new Date().getFullYear().toString(),
                                        "isbn": formData.isbn || "0000000000000",
                                        "description": formData.description,
                                        "items": [
                                            { "location": formData.location, "condition": formData.condition }
                                        ],
                                        "type": "other"
                                    }
                                ]

                            }
                        ]
                    });

                    // Reload books
                    const bookData = await getBooks(API_BASE, token, code_etablissement);
                    if (bookData && bookData.work) {
                        dispatch(setBooks(bookData.work));
                    }
                } catch (err) {
                    console.error(err);
                }
            })();
            onAdd({
                ...formData,
                id: Math.random().toString(36).substr(2, 9),
                rating: 0,
                status: 'Disponible',
                category: 'Autre',
                summary: formData.description
            });
        }

        //onClose();
        setFormData(prev => ({
            title: '', author: '', category: isCategoryLocked ? prev.category : '', cover: DEFAULT_COVER,
            issue: '', publisher: '', pubDate: '', description: '',
            issn: '', location: isLocationLocked ? prev.location : '', condition: '',
            isbn: '', language: 'fr', code_etablissement: code_etablissement
        }));
        setResourceType('book');

        // Focus back on ISBN scan input
        if (isbnInputRef.current) {
            setTimeout(() => {
                isbnInputRef.current.focus();
            }, 100);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-background border border-border rounded-2xl p-6 w-full max-w-4xl shadow-2xl overflow-y-auto max-h-[90vh]"
            >

                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-foreground">Ajouter un livre</h3>
                    <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
                        <X size={20} />
                    </button>
                </div>

                {/* Resource Type Selector */}
                <div className="flex p-1 bg-muted/50 rounded-xl mb-6 border border-border">
                    <button
                        onClick={() => setResourceType('book')}
                        className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all ${resourceType === 'book' ? 'bg-primary text-primary-foreground shadow-lg' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                            }`}
                    >
                        <BookOpen size={16} />
                        <span>Livre</span>
                    </button>
                    <button
                        onClick={() => setResourceType('review')}
                        className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all ${resourceType === 'review' ? 'bg-primary text-primary-foreground shadow-lg' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                            }`}
                    >
                        <FileText size={16} />
                        <span>Revue</span>
                    </button>
                    <button
                        onClick={() => setResourceType('other')}
                        className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all ${resourceType === 'other' ? 'bg-primary text-primary-foreground shadow-lg' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                            }`}
                    >
                        <Box size={16} />
                        <span>Autre</span>
                    </button>
                </div>

                {resourceType === 'book' && (
                    <div className="bg-black rounded-xl mb-6 relative overflow-hidden border-2 border-primary/30 flex flex-col">
                        <div className="relative flex items-center p-4 z-10">
                            <ScanLine className={`mr-3 ${isFetchingIsbn ? 'text-amber-500 animate-spin' : 'text-primary animate-pulse'}`} size={24} />
                            <input
                                ref={isbnInputRef}
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleSubmit(e);
                                    }
                                }}
                                type="text"
                                value={isbnScan}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setIsbnScan(val);

                                    if (scanTimeoutRef.current) {
                                        clearTimeout(scanTimeoutRef.current);
                                    }

                                    scanTimeoutRef.current = setTimeout(() => {
                                        if (val.length >= 10) {
                                            handleIsbnScan(val);
                                        }
                                    }, 300);
                                }}
                                disabled={isFetchingIsbn}
                                placeholder={isFetchingIsbn ? "Recherche en cours..." : "Scanner le code ISBN du livre.."}
                                className="w-full bg-transparent border-none focus:outline-none text-foreground font-mono text-lg placeholder:text-muted-foreground"
                            />

                            <button
                                type="button"
                                onClick={() => setIsScannerVisible(!isScannerVisible)}
                                className={`ml-3 p-2 rounded-full transition-colors ${isScannerVisible ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
                                    }`}
                                title="EXPERIMENTAL: Scanner avec la webcam"
                            >
                                <Camera size={20} />
                            </button>
                        </div>
                        {isScannerVisible && (
                            <div className="border-t border-border/50 bg-black/40">
                                <div style={{ padding: 16 }}>
                                    <h2 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                                        <ScanLine size={16} /> Scanner ISBN via Webcam
                                    </h2>

                                    <IsbnScanner
                                        onDetected={(value) => {
                                            setDatabarcode(value);
                                            setIsbnScan(value);
                                            handleIsbnScan(value);
                                            setIsScannerVisible(false); // Auto-hide after detection
                                        }}
                                        autoStop={true}
                                    />

                                    {databarcode && (
                                        <p className="mt-3 text-sm text-primary">
                                            ISBN détecté : <b>{databarcode}</b>
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                        {/* Placeholder content effect */}
                        <div className="relative p-4 flex items-center gap-4 border-t border-border bg-muted/30">
                            <div className="absolute inset-0 bg-primary/5 pointer-events-none animate-scan" style={{ animationDuration: '3s' }} />
                            <span className="text-xs text-muted-foreground mx-auto">Ou remplissez le formulaire ci-dessous</span>
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    {resourceType === 'book' ? (
                        <div className="flex gap-4">
                            <div className="w-24 h-36 flex-shrink-0 bg-muted rounded-lg overflow-hidden border border-border shadow-inner">
                                <img
                                    src={formData.cover || DEFAULT_COVER}
                                    alt="Preview"
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        e.currentTarget.onerror = null;
                                        e.currentTarget.src = DEFAULT_COVER;
                                    }}
                                />
                            </div>
                            <div className="flex-1 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-muted-foreground mb-1">Titre</label>
                                    <input
                                        required
                                        type="text"
                                        value={formData.title}
                                        onChange={(e) => setFormData({ ...formData, title: e.target.value, code_etablissement: currentUser.code_etablissement })}
                                        className="w-full bg-muted/50 border border-border rounded-xl py-2 px-4 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                        placeholder="Ex: Les Misérables"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-muted-foreground mb-1">Auteur</label>
                                    <input
                                        required
                                        type="text"
                                        value={formData.author}
                                        onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                                        className="w-full bg-muted/50 border border-border rounded-xl py-2 px-4 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <label className="block text-sm font-medium text-muted-foreground mb-1">Titre</label>
                            <input
                                required
                                type="text"
                                list={resourceType === 'review' ? "review-titles" : undefined}
                                value={formData.title}
                                onChange={(e) => {
                                    const newTitle = e.target.value;
                                    let newFormData = { ...formData, title: newTitle, code_etablissement: currentUser.code_etablissement };

                                    if (resourceType === 'review' && newTitle) {
                                        const existingReview = books.find(b => b.type === 'review' && b.title === newTitle);
                                        if (existingReview) {
                                            newFormData.category = existingReview.category || newFormData.category;
                                            newFormData.description = existingReview.description || existingReview.summary || newFormData.description;
                                            newFormData.publisher = existingReview.publisher || newFormData.publisher;
                                            newFormData.issn = existingReview.issn || newFormData.issn;
                                        }
                                    }
                                    setFormData(newFormData);
                                }}
                                className="w-full bg-muted/50 border border-border rounded-xl py-2 px-4 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                placeholder={resourceType === 'review' ? "Ex: Science & Vie" : "Ex: DVD, Carte.."}
                            />
                            {resourceType === 'review' && (
                                <datalist id="review-titles">
                                    {existingReviewTitles.map(title => (
                                        <option key={title} value={title} />
                                    ))}
                                </datalist>
                            )}
                        </div>
                    )}

                    {resourceType === 'book' && (
                        <>
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <div className="flex items-center gap-2">
                                        <label className="block text-sm font-medium text-muted-foreground">Catégorie</label>
                                        {formData.suggestedCategory && (
                                            <button
                                                type="button"
                                                onClick={() => setFormData({ ...formData, category: formData.suggestedCategory })}
                                                className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded-full hover:bg-primary/20 transition-colors flex items-center gap-1 border border-primary/20"
                                            >
                                                <CheckCircle size={10} /> Suggestion : {formData.suggestedCategory}
                                            </button>
                                        )}
                                    </div>
                                    {currentUser?.role === 'Bibliothécaire' && (
                                        <button
                                            type="button"
                                            onClick={onManageCategories}
                                            className="text-[10px] text-primary hover:underline flex items-center gap-1"
                                        >
                                            <Plus size={10} /> Gérer
                                        </button>
                                    )}
                                </div>
                                <div className="relative">
                                    <select
                                        disabled={isCategoryLocked}
                                        value={formData.category}
                                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                        className={`w-full bg-muted/50 border border-border rounded-xl py-2 pl-4 pr-16 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 relative appearance-none ${isCategoryLocked ? 'border-amber-500/50 ring-1 ring-amber-500/20 opacity-80 cursor-not-allowed' : ''}`}
                                    >
                                        <option value="">Sélectionner...</option>
                                        {[...categoryBooks].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((category) => (
                                            <option key={category.ID || category.name} value={category.name}>
                                                {category.name}
                                            </option>
                                        ))}
                                        {formData.category && !categoryBooks.some(c => c.name === formData.category) && (
                                            <option value={formData.category}>{formData.category} (Nouveau)</option>
                                        )}
                                    </select>

                                    {/* Chevron for select (since appearance-none removes it) */}
                                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground">
                                        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>

                                    <button
                                        type="button"
                                        disabled={!formData.category && !isCategoryLocked}
                                        onClick={() => setIsCategoryLocked(!isCategoryLocked)}
                                        className={`absolute right-10 top-1/2 -translate-y-1/2 p-1.5 rounded-full transition-all z-10 ${!formData.category
                                            ? 'text-muted-foreground/30 cursor-not-allowed'
                                            : isCategoryLocked
                                                ? 'text-amber-500 bg-amber-500/10 hover:bg-amber-500/20'
                                                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                            }`}
                                        title={isCategoryLocked ? "Catégorie verrouillée" : "Verrouiller la catégorie"}
                                    >
                                        {isCategoryLocked ? <Lock size={14} /> : <Unlock size={14} />}
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-muted-foreground mb-1">ISBN</label>
                                    <input
                                        type="text"
                                        value={formData.isbn}
                                        onChange={(e) => setFormData({ ...formData, isbn: e.target.value })}
                                        className="w-full bg-muted/50 border border-border rounded-xl py-2 px-4 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                        placeholder="Ex: 978-2..."
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-muted-foreground mb-1">Langue</label>
                                        <input
                                            type="text"
                                            value={formData.language}
                                            onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                                            className="w-full bg-muted/50 border border-border rounded-xl py-2 px-4 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                            placeholder="fr"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-muted-foreground mb-1">État</label>
                                        <select
                                            value={formData.condition}
                                            onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                                            className="w-full bg-muted/50 border border-border rounded-xl py-2 px-4 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                        >
                                            <option value="Neuf">Neuf</option>
                                            <option value="Très bon">Très bon</option>
                                            <option value="Bon">Bon</option>
                                            <option value="Moyen">Moyen</option>
                                            <option value="Mauvais">Mauvais</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-muted-foreground mb-1">Éditeur</label>
                                    <input
                                        type="text"
                                        value={formData.publisher}
                                        onChange={(e) => setFormData({ ...formData, publisher: e.target.value })}
                                        className="w-full bg-muted/50 border border-border rounded-xl py-2 px-4 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-muted-foreground mb-1">Date de publication</label>
                                    <input
                                        type="text"
                                        value={formData.pubDate}
                                        onChange={(e) => setFormData({ ...formData, pubDate: e.target.value })}
                                        className="w-full bg-muted/50 border border-border rounded-xl py-2 px-4 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                        placeholder="Ex: 2024"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-muted-foreground mb-1">Emplacement</label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            list="add-locations-list"
                                            value={formData.location}
                                            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                            className={`w-full bg-muted/50 border border-border rounded-xl py-2 pl-4 pr-10 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 ${isLocationLocked ? 'border-amber-500/50 ring-1 ring-amber-500/20' : ''}`}
                                            placeholder="Sélectionner ou saisir..."
                                        />
                                        <button
                                            type="button"
                                            disabled={!formData.location && !isLocationLocked}
                                            onClick={() => { if (isLocationLocked) setFormData({ ...formData, location: "" }); setIsLocationLocked(!isLocationLocked); }}
                                            className={`absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full transition-all z-10 ${!formData.location
                                                ? 'text-muted-foreground/30 cursor-not-allowed'
                                                : isLocationLocked
                                                    ? 'text-amber-500 bg-amber-500/10 hover:bg-amber-500/20'
                                                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                                }`}
                                            title={isLocationLocked ? "Emplacement verrouillé" : "Verrouiller l'emplacement"}
                                        >
                                            {isLocationLocked ? <Lock size={14} /> : <Unlock size={14} />}
                                        </button>
                                        <datalist id="add-locations-list">
                                            {uniqueLocations.map(loc => (
                                                <option key={loc} value={loc} />
                                            ))}
                                        </datalist>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-muted-foreground mb-1">Code Établissement</label>
                                    <input
                                        type="text"
                                        value={formData.code_etablissement}
                                        onChange={(e) => setFormData({ ...formData, code_etablissement: e.target.value })}
                                        className="w-full bg-muted/50 border border-border rounded-xl py-2 px-4 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-muted-foreground mb-1">Description / Résumé</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full bg-muted/50 border border-border rounded-xl py-2 px-4 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 min-h-[120px] resize-none"
                                    placeholder="Résumé du livre..."
                                />
                            </div>
                        </>
                    )}

                    {/* Conditional Fields: Review */}
                    {resourceType === 'review' && (
                        <>
                            <div>
                                <label className="block text-sm font-medium text-muted-foreground mb-1">Numéro / Édition</label>
                                <input
                                    required
                                    type="text"
                                    value={formData.tag}
                                    onChange={(e) => setFormData({ ...formData, tag: e.target.value })}
                                    className="w-full bg-muted/50 border border-border rounded-xl py-2 px-4 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    placeholder="Ex: N° 312 - Janvier 2024"
                                />
                            </div>
                            <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                    <label className="block text-sm font-medium text-muted-foreground">Catégorie</label>
                                    {formData.suggestedCategory && (
                                        <button
                                            type="button"
                                            onClick={() => setFormData({ ...formData, category: formData.suggestedCategory })}
                                            className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded-full hover:bg-primary/20 transition-colors flex items-center gap-1 border border-primary/20"
                                        >
                                            <CheckCircle size={10} /> Suggestion : {formData.suggestedCategory}
                                        </button>
                                    )}
                                </div>
                                {currentUser?.role === 'Bibliothécaire' && (
                                    <button
                                        type="button"
                                        onClick={onManageCategories}
                                        className="text-[10px] text-primary hover:underline flex items-center gap-1"
                                    >
                                        <Plus size={10} /> Gérer
                                    </button>
                                )}
                            </div>
                            <div className="relative">
                                <select
                                    disabled={isCategoryLocked}
                                    value={formData.category}
                                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                    className={`w-full bg-muted/50 border border-border rounded-xl py-2 pl-4 pr-16 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 relative appearance-none ${isCategoryLocked ? 'border-amber-500/50 ring-1 ring-amber-500/20 opacity-80 cursor-not-allowed' : ''}`}
                                >
                                    <option value="">Sélectionner...</option>
                                    {[...categoryBooks].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((category) => (
                                        <option key={category.ID || category.name} value={category.name}>
                                            {category.name}
                                        </option>
                                    ))}
                                    {formData.category && !categoryBooks.some(c => c.name === formData.category) && (
                                        <option value={formData.category}>{formData.category} (Nouveau)</option>
                                    )}
                                </select>

                                {/* Chevron for select (since appearance-none removes it) */}
                                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground">
                                    <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>

                                <button
                                    type="button"
                                    disabled={!formData.category && !isCategoryLocked}
                                    onClick={() => setIsCategoryLocked(!isCategoryLocked)}
                                    className={`absolute right-10 top-1/2 -translate-y-1/2 p-1.5 rounded-full transition-all z-10 ${!formData.category
                                        ? 'text-muted-foreground/30 cursor-not-allowed'
                                        : isCategoryLocked
                                            ? 'text-amber-500 bg-amber-500/10 hover:bg-amber-500/20'
                                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                        }`}
                                    title={isCategoryLocked ? "Catégorie verrouillée" : "Verrouiller la catégorie"}
                                >
                                    {isCategoryLocked ? <Lock size={14} /> : <Unlock size={14} />}
                                </button>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-muted-foreground mb-1">ISSN</label>
                                <input
                                    type="text"
                                    value={formData.issn}
                                    onChange={(e) => setFormData({ ...formData, issn: e.target.value })}
                                    className="w-full bg-muted/50 border border-border rounded-xl py-2 px-4 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    placeholder="Ex: 1234-5678"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-muted-foreground mb-1">Éditeur</label>
                                    <input
                                        type="text"
                                        value={formData.publisher}
                                        onChange={(e) => setFormData({ ...formData, publisher: e.target.value })}
                                        className="w-full bg-muted/50 border border-border rounded-xl py-2 px-4 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-muted-foreground mb-1">Date</label>
                                    <input
                                        type="date"
                                        value={formData.pubDate}
                                        onChange={(e) => setFormData({ ...formData, pubDate: e.target.value })}
                                        className="w-full bg-muted/50 border border-border rounded-xl py-2 px-4 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-muted-foreground mb-1">Emplacement</label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={formData.location}
                                            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                            className={`w-full bg-muted/50 border border-border rounded-xl py-2 pl-4 pr-10 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 ${isLocationLocked ? 'border-amber-500/50 ring-1 ring-amber-500/20' : ''}`}
                                        />
                                        <button
                                            type="button"
                                            disabled={!formData.location && !isLocationLocked}
                                            onClick={() => setIsLocationLocked(!isLocationLocked)}
                                            className={`absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full transition-all z-10 ${!formData.location
                                                ? 'text-muted-foreground/30 cursor-not-allowed'
                                                : isLocationLocked
                                                    ? 'text-amber-500 bg-amber-500/10 hover:bg-amber-500/20'
                                                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                                }`}
                                            title={isLocationLocked ? "Emplacement verrouillé" : "Verrouiller l'emplacement"}
                                        >
                                            {isLocationLocked ? <Lock size={14} /> : <Unlock size={14} />}
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-muted-foreground mb-1">État</label>
                                    <select
                                        value={formData.condition}
                                        onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                                        className="w-full bg-muted/50 border border-border rounded-xl py-2 px-4 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    >
                                        <option value="Neuf">Neuf</option>
                                        <option value="Très bon">Très bon</option>
                                        <option value="Bon">Bon</option>
                                        <option value="Moyen">Moyen</option>
                                        <option value="Mauvais">Mauvais</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-muted-foreground mb-1">Description / Résumé</label>
                                    <textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        className="w-full bg-muted/50 border border-border rounded-xl py-2 px-4 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 min-h-[120px] resize-none"
                                        placeholder="Description de cette revue..."
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    {/* Conditional Fields: Other */}
                    {resourceType === 'other' && (
                        <>
                            <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                    <label className="block text-sm font-medium text-muted-foreground">Catégorie</label>
                                    {formData.suggestedCategory && (
                                        <button
                                            type="button"
                                            onClick={() => setFormData({ ...formData, category: formData.suggestedCategory })}
                                            className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded-full hover:bg-primary/20 transition-colors flex items-center gap-1 border border-primary/20"
                                        >
                                            <CheckCircle size={10} /> Suggestion : {formData.suggestedCategory}
                                        </button>
                                    )}
                                </div>
                                {currentUser?.role === 'Bibliothécaire' && (
                                    <button
                                        type="button"
                                        onClick={onManageCategories}
                                        className="text-[10px] text-primary hover:underline flex items-center gap-1"
                                    >
                                        <Plus size={10} /> Gérer
                                    </button>
                                )}
                            </div>
                            <div className="relative mb-4">
                                <select
                                    disabled={isCategoryLocked}
                                    value={formData.category}
                                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                    className={`w-full bg-muted/50 border border-border rounded-xl py-2 pl-4 pr-16 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 relative appearance-none ${isCategoryLocked ? 'border-amber-500/50 ring-1 ring-amber-500/20 opacity-80 cursor-not-allowed' : ''}`}
                                >
                                    <option value="">Sélectionner...</option>
                                    {[...categoryBooks].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((category) => (
                                        <option key={category.ID || category.name} value={category.name}>
                                            {category.name}
                                        </option>
                                    ))}
                                    {formData.category && !categoryBooks.some(c => c.name === formData.category) && (
                                        <option value={formData.category}>{formData.category} (Nouveau)</option>
                                    )}
                                </select>

                                {/* Chevron for select (since appearance-none removes it) */}
                                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground">
                                    <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>

                                <button
                                    type="button"
                                    disabled={!formData.category && !isCategoryLocked}
                                    onClick={() => setIsCategoryLocked(!isCategoryLocked)}
                                    className={`absolute right-10 top-1/2 -translate-y-1/2 p-1.5 rounded-full transition-all z-10 ${!formData.category
                                        ? 'text-muted-foreground/30 cursor-not-allowed'
                                        : isCategoryLocked
                                            ? 'text-amber-500 bg-amber-500/10 hover:bg-amber-500/20'
                                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                        }`}
                                    title={isCategoryLocked ? "Catégorie verrouillée" : "Verrouiller la catégorie"}
                                >
                                    {isCategoryLocked ? <Lock size={14} /> : <Unlock size={14} />}
                                </button>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-muted-foreground mb-1">Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full bg-muted/50 border border-border rounded-xl py-2 px-4 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 min-h-[100px]"
                                    placeholder="Détails sur la ressource..."
                                />
                            </div>
                        </>
                    )}

                    <button
                        type="submit"
                        className="w-full bg-primary text-primary-foreground font-bold py-3 rounded-xl hover:bg-primary/90 transition-colors mt-4"
                    >
                        {resourceType === 'book' ? 'Ajouter le livre' : resourceType === 'review' ? 'Ajouter la revue' : 'Ajouter'}
                    </button>
                </form>
            </motion.div >
        </div >
    );
};



const CategoryManagerModal = ({ isOpen, onClose }) => {
    const dispatch = useDispatch();
    const { categoryBooks } = useSelector((state) => state.books);
    const API_BASE = useSelector((state) => state.user.apiBase);
    const token = useSelector((state) => state.user.currentUser.token);
    const code_etablissement = useSelector((state) => state.user.currentUser.code_etablissement);
    const [newCategory, setNewCategory] = useState('');
    const [editingCategory, setEditingCategory] = useState(null);
    const [editName, setEditName] = useState('');

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!newCategory.trim()) return;
        await dispatch(addCategoryThunk({ API_BASE, token, name: newCategory.trim(), code_etablissement }));
        setNewCategory('');
    };

    const handleUpdate = async (cat) => {
        if (!editName.trim() || editName === cat.name) {
            setEditingCategory(null);
            return;
        }
        await dispatch(updateCategoryThunk({ API_BASE, token, id: cat.ID, name: editName.trim() }));
        setEditingCategory(null);
    };

    const handleDelete = async (id) => {
        if (window.confirm("Supprimer cette catégorie ?")) {
            await dispatch(deleteCategoryThunk({ API_BASE, token, id }));
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-background border border-border rounded-2xl p-6 w-full max-w-lg shadow-2xl flex flex-col max-h-[80vh]"
            >
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-foreground">Gérer les catégories</h3>
                    <button onClick={onClose} className="p-2 text-muted-foreground hover:text-foreground transition-colors">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleAdd} className="flex gap-2 mb-6">
                    <input
                        type="text"
                        value={newCategory}
                        onChange={(e) => setNewCategory(e.target.value)}
                        placeholder="Nouvelle catégorie..."
                        className="flex-1 bg-muted/50 border border-border rounded-xl px-4 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                    <button
                        type="submit"
                        className="bg-primary text-primary-foreground p-2 rounded-xl hover:bg-primary/90 transition-colors"
                    >
                        <Plus size={20} />
                    </button>
                </form>

                <div className="flex-1 overflow-y-auto space-y-2 pr-2">
                    {[...categoryBooks].sort((a, b) => (a.name || "").localeCompare(b.name || "")).filter((cat) => cat.name.toLowerCase().includes(newCategory.toLowerCase())).map((cat) => (
                        <div key={cat.ID} className="flex items-center justify-between p-3 rounded-xl bg-muted/30 border border-border group">
                            {editingCategory === cat.ID ? (
                                <input
                                    autoFocus
                                    type="text"
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    onBlur={() => handleUpdate(cat)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleUpdate(cat)}
                                    className="flex-1 bg-background border border-primary/50 rounded-lg px-2 py-1 text-sm text-foreground focus:outline-none"
                                />
                            ) : (
                                <span className="text-foreground font-medium">{cat.name}</span>
                            )}

                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                    onClick={() => {
                                        setEditingCategory(cat.ID);
                                        setEditName(cat.name);
                                    }}
                                    className="p-1.5 text-blue-400 hover:bg-blue-400/10 rounded-lg transition-colors"
                                >
                                    <Pencil size={16} />
                                </button>
                                <button
                                    onClick={() => handleDelete(cat.ID)}
                                    className="p-1.5 text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                                >
                                    <Trash size={16} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </motion.div>
        </div>
    );
};

export default function Books() {
    const dispatch = useDispatch();
    const { items: books, searchQuery, currentBook, categoryBooks } = useSelector((state) => state.books);
    const { currentUser } = useSelector((state) => state.user);

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isMemberSelectionOpen, setIsMemberSelectionOpen] = useState(false);
    const [isCategoryManagerOpen, setIsCategoryManagerOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [sortOrder, setSortOrder] = useState('recent'); // 'recent', 'most_borrowed', 'least_borrowed'
    const [hideDuplicates, setHideDuplicates] = useState(false);
    const [loans, setLoans] = useState([]);

    const [printQRBook, setPrintQRBook] = useState(null);
    const [selectedMember, setSelectedMember] = useState(null);
    const [bookToLoan, setBookToLoan] = useState(null);
    const [selectionMode, setSelectionMode] = useState('loan'); // 'loan' or 'reservation'
    //const [numberBooksPerPage, setNumberBooksPerPage] = useState(10);
    const [visibleBooks, setVisibleBooks] = useState(10);

    const API_BASE = useSelector((state) => state.user.apiBase);
    const code_etablissement = useSelector((state) => state.user.currentUser.code_etablissement);
    const token = useSelector((state) => state.user.currentUser.token);
    const [printQRMember, setPrintQRMember] = useState(null);
    const observerRef = useRef(null);
    const dateInputRef = useRef(null);

    // Confirmation Modal State
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: null,
        isDanger: false,
        confirmText: 'Confirmer'
    });



    useEffect(() => {
        dispatch(fetchCategories({ API_BASE, token, code_etablissement }));
        const loadBooks = async () => {
            try {
                const bookData = await getBooks(API_BASE, token, code_etablissement);
                dispatch(setBooks(bookData.work));
            } catch (err) {
                console.error("Failed to load books", err);
            }
        };

        const loadLoansData = async () => {
            try {
                const loansData = await getLoansList(API_BASE, token, code_etablissement);
                setLoans(loansData || []);
            } catch (err) {
                console.error("Failed to load loans history", err);
            }
        };

        loadBooks();
        loadLoansData();
    }, [dispatch, API_BASE, token, code_etablissement]);

    const borrowCounts = useMemo(() => {
        const counts = {};
        loans.forEach(loan => {
            const itemId = loan.item_id?.toString();
            if (itemId) {
                counts[itemId] = (counts[itemId] || 0) + 1;
            }
        });
        return counts;
    }, [loans]);

    const filteredBooks = useMemo(() => {
        let filtered = books.filter(book => {
            if (searchQuery.toLowerCase().includes("biblius:multiples")) {
                if (book.baffiche == true) return true;
                return false;
            }

            const matchesSearch = (book.title || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
                (book.author || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
                (book.isbn || "").toLowerCase().includes(searchQuery.replaceAll("-", "")) ||
                (book.description || "").toLowerCase().includes(searchQuery.toLowerCase());

            const matchesCategory = selectedCategory ? book.category === selectedCategory : true;
            const matchesDate = dateFilter ? (book.CreatedAt && new Date(book.CreatedAt).getTime() > new Date(dateFilter).getTime()) : true;

            return matchesSearch && matchesCategory && matchesDate;
        });

        // Application du masquage des doublons
        if (hideDuplicates) {
            const manifestationGroups = {};
            filtered.forEach(book => {
                const mId = book.manifestation_id || 'no-id-' + (book.item_id || book.ID || book.id);
                if (!manifestationGroups[mId]) {
                    manifestationGroups[mId] = [];
                }
                manifestationGroups[mId].push(book);
            });

            filtered = Object.values(manifestationGroups).map(group => {
                // On privilégie un exemplaire disponible pour l'affichage
                const representative = group.find(b => b.status === 'Disponible') || group[0];
                return {
                    ...representative,
                    copiesCount: group.length
                };
            });
        }

        // Application du tri
        if (sortOrder === 'most_borrowed') {
            return [...filtered].sort((a, b) => {
                const countA = borrowCounts[a.item_id || a.ID] || 0;
                const countB = borrowCounts[b.item_id || b.ID] || 0;
                if (countB !== countA) return countB - countA;

                // Stabilization avec date de création si counts égaux
                const dateB = b.CreatedAt ? new Date(b.CreatedAt).getTime() : 0;
                const dateA = a.CreatedAt ? new Date(a.CreatedAt).getTime() : 0;
                return (isNaN(dateB) ? 0 : dateB) - (isNaN(dateA) ? 0 : dateA);
            });
        } else if (sortOrder === 'least_borrowed') {
            return [...filtered].sort((a, b) => {
                const countA = borrowCounts[a.item_id || a.ID] || 0;
                const countB = borrowCounts[b.item_id || b.ID] || 0;
                if (countB !== countA) return countA - countB;

                // Stabilization avec date de création si counts égaux
                const dateB = b.CreatedAt ? new Date(b.CreatedAt).getTime() : 0;
                const dateA = a.CreatedAt ? new Date(a.CreatedAt).getTime() : 0;
                return (isNaN(dateB) ? 0 : dateB) - (isNaN(dateA) ? 0 : dateA);
            });
        }

        return filtered;
    }, [books, searchQuery, selectedCategory, dateFilter, sortOrder, borrowCounts, hideDuplicates]);

    useEffect(() => {
        if (searchQuery.toLowerCase().includes("biblius:multiples")) {
            setVisibleBooks(filteredBooks.length || 10);
        } else {
            setVisibleBooks(10);
        }

    }, [searchQuery, filteredBooks.length, sortOrder, selectedCategory, dateFilter]);

    const handleAddBook = (newBook) => {
        dispatch(addBook(newBook));
    };

    const handleDuplicateFound = (book) => {
        setConfirmModal({
            isOpen: true,
            title: "Livre déjà présent",
            message: `Ce livre (ISBN: ${book.isbn || (book.expressions?.[0]?.manifestations?.[0]?.isbn) || 'N/A'}) existe déjà dans votre bibliothèque. Nous allons vous rediriger vers la fiche d'un livre ayant le même ISBN.`,
            onConfirm: () => {
                dispatch(setCurrentBook(book));
                setIsAddModalOpen(false);
            },
            isDanger: false,
            confirmText: "Voir la fiche"
        });
    };

    const handleUpdateCover = (id, cover) => {
        dispatch(updateBookCover({ id, cover }));
    };

    const handleLoanClick = (book) => {
        setBookToLoan(book);
        setSelectionMode('loan');
        setIsMemberSelectionOpen(true);
    };

    const handleSelectMember = async (member) => {
        if (selectionMode === 'loan') {
            if (bookToLoan && member) {
                try {
                    await createLoan(API_BASE, token, {
                        item_id: parseInt(bookToLoan.item_id || bookToLoan.ID, 10),
                        user_id: parseInt(member.id || member.ID, 10),
                        start_date: new Date().toISOString(),
                        end_date: null,
                        code_etablissement: code_etablissement
                    });

                    // Update books list locally
                    dispatch(loanBook({
                        bookId: bookToLoan.ID,
                        borrower: member
                    }));

                    // Update currentBook if it's the one we just loaned
                    if (currentBook && (currentBook.ID === bookToLoan.ID)) {
                        dispatch(setCurrentBook({ ...currentBook, status: 'Emprunté', borrower: member }));
                    }

                    setIsMemberSelectionOpen(false);
                    //alert("Livre prêté avec succès !");
                } catch (error) {
                    console.error("Loan failed:", error);
                    alert("Erreur lors du prêt du livre.");
                }
            }
        } else if (selectionMode === 'reservation') {
            if (bookToLoan && member) {
                try {
                    await createReservation(API_BASE, token, {
                        item_id: parseInt(bookToLoan.item_id || bookToLoan.id || bookToLoan.ID, 10),
                        user_id: parseInt(member.id || member.ID, 10),
                        code_etablissement: code_etablissement
                    });
                    alert(`Livre réservé pour ${member.name}`);
                    setIsMemberSelectionOpen(false);
                    // Refresh book detail modal by re-setting currentBook to itself (will trigger useEffect)
                    const updatedBook = { ...currentBook };
                    dispatch(setCurrentBook(null));
                    setTimeout(() => dispatch(setCurrentBook(updatedBook)), 50);
                } catch (error) {
                    console.error("Reservation failed:", error);
                    alert("Erreur lors de la réservation : " + error.message);
                }
            }
        }
    };





    // Create PDF with label dimensions 69mm x 100mm
    const handleBulkPrint = () => {
        const doc = new jsPDF({
            orientation: 'p', // Portrait because 69x100 is typically portrait orientation for label rolls
            unit: 'mm',
            format: [50, 50]
        });

        filteredBooks.forEach((book, index) => {
            if (index > 0) {
                doc.addPage([50, 50], 'p');
            }

            const qrcodeData = book.qrcode_data;
            const title = book.title;
            const id = book.ID || book.id;
            const role = null; // Or fetch/deduce if needed
            const classe = null;

            // Add QR Code (Base64)
            if (qrcodeData) {
                const imgData = "data:image/png;base64," + qrcodeData;
                doc.addImage(imgData, 'PNG', 8, 3, 34, 34); // Centered horizontally (69-61)/2 = 4mm margin
            }

            // Add Establishment Code
            doc.setFontSize(10);
            doc.setTextColor(100);
            const etabText = code_etablissement;
            const etabWidth = doc.getTextWidth(etabText);
            doc.text(etabText, (50 - etabWidth) / 2, 6);

            // Add Item ID
            doc.setFontSize(12);
            doc.setTextColor(0);
            doc.setFont(undefined, 'bold');
            const idText = `ID: ${id}`;
            const idWidth = doc.getTextWidth(idText);
            doc.text(idText, (50 - idWidth) / 2, 38);

            // Add Truncated Title
            doc.setFontSize(9);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(80);
            const splitTitle = doc.splitTextToSize(title || "", 46); // 60mm width for text
            let yPos = 42
            splitTitle.forEach(line => {
                const lineWidth = doc.getTextWidth(line);
                doc.text(line, (50 - lineWidth) / 2, yPos);
                yPos += 4;
            });
        });

        // Save the PDF
        doc.save(`etiquettes_date_${dateFilter || 'tous'}.pdf`);
    };

    const sentinelRef = useCallback(node => {
        if (observerRef.current) observerRef.current.disconnect();

        if (node) {
            observerRef.current = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting) {
                        setVisibleBooks(prev => {
                            if (prev < filteredBooks.length) {
                                return prev + 10;
                            }
                            return prev;
                        });
                    }
                },
                { rootMargin: '150px' }
            );
            observerRef.current.observe(node);
        }
    }, [filteredBooks.length]);

    const handlePrintList = () => {
        const doc = new jsPDF();

        doc.setFontSize(16);
        doc.text("Liste des livres", 14, 15);

        doc.setFontSize(10);
        doc.setTextColor(100);
        const subtitle = selectedCategory ? `Catégorie: ${selectedCategory} (${filteredBooks.length} livres)` : `Toutes les catégories (${filteredBooks.length} livres)`;
        doc.text(subtitle, 14, 22);

        const tableColumn = ["ID", "Titre", "Auteur", "Catégorie", "Éditeur", "Date", "Statut"];
        const tableRows = [];

        filteredBooks.forEach(book => {
            const bookData = [
                book.item_id || book.id || book.ID || "-",
                book.title || "-",
                book.author || "-",
                book.category || "-",
                book.publisher || "-",
                book.year || "-",
                book.status || "-"
            ];
            tableRows.push(bookData);
        });

        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: 28,
            styles: { fontSize: 8 },
            headStyles: { fillColor: [41, 128, 185] },
        });

        doc.save(`liste_livres_${selectedCategory ? selectedCategory.replace(/[^a-z0-9]/gi, '_').toLowerCase() : 'tous'}.pdf`);
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-3xl font-bold text-foreground">Gestion de la Bibliothèque ({filteredBooks.length} {filteredBooks.length > 1 ? 'livres' : 'livre'})</h2>
                    <p className="text-muted-foreground mt-1">Gérer les livres, les revues et autres ressources</p>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={handlePrintList}
                        className="flex items-center gap-2 bg-muted text-foreground px-4 py-2 rounded-xl font-medium hover:bg-muted/80 border border-border transition-colors"
                        title="Imprimer la liste au format PDF"
                    >
                        <Printer size={18} />
                        <span className="hidden md:inline">Imprimer la liste</span>
                    </button>
                    {currentUser.role == 'Bibliothécaire' && (
                        <button
                            onClick={() => setIsAddModalOpen(true)}
                            className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-xl font-medium hover:bg-primary/90 transition-colors"
                        >
                            <Plus size={18} />
                            <span>Ajouter un livre</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Search Bar & Filter */}
            <div className="flex flex-col md:flex-row gap-4">
                <div className="relative group flex-1">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors" size={20} />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => dispatch(setSearchQuery(e.target.value))}
                        placeholder="Rechercher dans titre, auteur, résumé"
                        className="w-full bg-muted/50 backdrop-blur-xl border border-border rounded-2xl py-4 pl-12 pr-4 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                    />
                </div>

                <div className="min-w-[200px]">
                    <div className="relative">
                        {/*<Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />*/}
                        <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            className="w-full bg-muted/50 backdrop-blur-xl border border-border rounded-2xl py-4 pl-12 pr-10 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all appearance-none cursor-pointer"
                        >
                            <option value="">Toutes les catégories</option>
                            {[...categoryBooks].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map(cat => (
                                <option key={cat.ID || cat.id || cat.name} value={cat.name}>{cat.name}</option>
                            ))}
                        </select>
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                            <Filter size={16} className="text-muted-foreground" />
                        </div>
                    </div>
                </div>
                <div className="min-w-[220px]">
                    <div className="relative">
                        <select
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                            className="w-full bg-muted/50 backdrop-blur-xl border border-border rounded-2xl py-4 pl-12 pr-10 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all appearance-none cursor-pointer"
                        >
                            <option value="recent">Plus récents</option>
                            <option value="most_borrowed">Plus empruntés</option>
                            <option value="least_borrowed">Moins empruntés</option>
                        </select>
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                            <TrendingUp size={16} className="text-muted-foreground" />
                        </div>
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                            <ArrowUpDown size={20} className="text-muted-foreground" />
                        </div>
                    </div>
                </div>

                <div className="flex items-center">
                    <button
                        onClick={() => setHideDuplicates(!hideDuplicates)}
                        className={`group relative flex items-center gap-3 px-6 py-4 rounded-2xl border transition-all duration-300 ${hideDuplicates
                            ? 'bg-primary/20 border-primary text-primary font-bold shadow-lg shadow-primary/10'
                            : 'bg-muted/30 border-border text-muted-foreground hover:border-primary/50 hover:bg-muted/50'
                            }`}
                    >
                        {hideDuplicates ? (
                            <Box size={20} className="animate-in zoom-in duration-300" />
                        ) : (
                            <Copy size={20} className="transition-transform group-hover:scale-110" />
                        )}
                        <span className="whitespace-nowrap flex flex-col items-start leading-none">
                            <span className="text-sm">{hideDuplicates ? 'Afficher les doublons' : 'Masquer les doublons'}</span>
                        </span>
                    </button>
                </div>


                <div className="min-w-[200px]">
                    <div className="relative">
                        <Calendar
                            className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground cursor-pointer z-10 hover:text-primary transition-colors"
                            size={20}
                            onClick={() => {
                                if (dateInputRef.current) {
                                    dateInputRef.current.showPicker();
                                }
                            }}
                        />

                        <input
                            ref={dateInputRef}
                            type="date"
                            value={dateFilter}
                            onChange={(e) => setDateFilter(e.target.value)}
                            className="w-full bg-muted/50 backdrop-blur-xl border border-border rounded-2xl py-4 pl-12 pr-12 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all cursor-pointer [&::-webkit-calendar-picker-indicator]:hidden"
                        />

                        {(currentUser.role == 'Bibliothécaire' && dateFilter) && (
                            <button
                                onClick={handleBulkPrint}
                                className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-primary/10 text-primary hover:bg-primary/20 rounded-lg transition-colors z-20"
                                title="Imprimer les étiquettes filtrées"
                            >
                                <Printer size={18} />
                            </button>
                        )}

                        <p className="absolute -top-4 left-[-5px] text-[9px] font-bold text-muted-foreground w-full pl-4">
                            LIVRES ENTRÉS EN BIBLIOTHÈQUE APRÈS LE
                        </p>
                    </div>
                </div>

            </div>

            {/* Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                <AnimatePresence mode="popLayout">
                    {filteredBooks.slice(0, visibleBooks).map((book, index) => (
                        <div
                            key={book.ID || book.id}
                        >
                            <BookCard
                                {...book}
                                currentUserRole={currentUser.role}
                                onClick={() => {
                                    //console.log("Click on book:", book);
                                    console.log("Click on currentBook:", book);
                                    if (currentBook && (currentBook.ID === book.ID)) {
                                        dispatch(setCurrentBook(null));
                                    } else {
                                        dispatch(setCurrentBook(book));
                                    }
                                }}
                                onPrintQR={setPrintQRBook}
                                delay={Math.min((index % 10) * 0.05, 0.3)}
                            />
                        </div>
                    ))}
                </AnimatePresence>
            </div>

            {/* Sentinel for Infinite Scroll with Loading Indicator */}
            {visibleBooks < filteredBooks.length && (
                <div ref={sentinelRef} className="h-20 w-full flex items-center justify-center mt-6">
                    <div className="flex items-center gap-2 text-muted-foreground bg-card/50 backdrop-blur-md px-4 py-2 rounded-full border border-border shadow-lg animate-pulse">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent" />
                        <span className="text-sm font-medium">Chargement des livres suivants...</span>
                    </div>
                </div>
            )}

            <AddBookModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onAdd={handleAddBook}
                onDuplicateFound={handleDuplicateFound}
                currentUser={currentUser}
                onManageCategories={() => setIsCategoryManagerOpen(true)}
                code_etablissement={currentUser.code_etablissement}
            />

            <BookDetailsModal
                isOpen={!!currentBook}
                currentUser={currentUser}
                onClose={() => dispatch(setCurrentBook(null))}
                book={currentBook}
                onUpdateCover={handleUpdateCover}
                onMemberClick={(member) => {
                    dispatch(setCurrentBook(null));
                    setSelectedMember(member);
                }}
                onLoanClick={handleLoanClick}
                onReserveForMemberClick={(book) => {
                    setBookToLoan(book);
                    setSelectionMode('reservation');
                    setIsMemberSelectionOpen(true);
                }}
                onPrintQR={setPrintQRBook}

                onManageCategories={() => setIsCategoryManagerOpen(true)}
            />

            <CategoryManagerModal
                isOpen={isCategoryManagerOpen}
                onClose={() => setIsCategoryManagerOpen(false)}
            />

            <MemberSelectionModal
                isOpen={isMemberSelectionOpen}
                onClose={() => setIsMemberSelectionOpen(false)}
                onSelect={handleSelectMember}
            />

            <MemberDetailsModal
                isOpen={!!selectedMember}
                onClose={() => setSelectedMember(null)}
                member={selectedMember}
                currentUser={currentUser}
                onPrintQR={setPrintQRMember}
            />

            <PrintQRModal
                isOpen={!!printQRBook}
                onClose={() => setPrintQRBook(null)}
                title={printQRBook?.title}
                id={printQRBook?.ID || printQRBook?.id}
                qrcodeData={printQRBook?.qrcode_data}
                codeEtablissement={currentUser?.code_etablissement}
                role={null}
                classe={null}
            />

            <PrintQRModal
                isOpen={!!printQRMember}
                onClose={() => setPrintQRMember(null)}
                title={printQRMember?.name}
                id={printQRMember?.ID || printQRMember?.id}
                qrcodeData={printQRMember?.qrcode_data}
                codeEtablissement={currentUser?.code_etablissement}
                role={printQRMember?.role}
                classe={printQRMember?.classe}
            />

            <ConfirmationModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
                onConfirm={confirmModal.onConfirm}
                title={confirmModal.title}
                message={confirmModal.message}
                isDanger={confirmModal.isDanger}
                confirmText={confirmModal.confirmText}
            />
        </div>
    );
}
